// Backend integration point: persist language preference to workspaces.default_language via Supabase

export type Lang = 'en' | 'ar';

export const translations = {
  en: {
    // Auth
    signIn: 'Sign In',
    signUp: 'Sign Up',
    email: 'Email address',
    password: 'Password',
    confirmPassword: 'Confirm password',
    fullName: 'Full name',
    continueWithGoogle: 'Continue with Google',
    alreadyHaveAccount: 'Already have an account?',
    dontHaveAccount: "Don't have an account?",
    forgotPassword: 'Forgot password?',
    orContinueWith: 'or continue with',
    rememberMe: 'Remember me',
    signingIn: 'Signing in…',
    creatingAccount: 'Creating account…',
    invalidCredentials: 'Invalid credentials — use the demo accounts below to sign in',
    emailRequired: 'Email is required',
    emailInvalid: 'Enter a valid email address',
    passwordRequired: 'Password is required',
    passwordMin: 'Password must be at least 8 characters',
    nameRequired: 'Full name is required',
    passwordMismatch: 'Passwords do not match',
    demoCredentials: 'Demo credentials',
    useDemoAccount: 'Use demo account',
    // Onboarding
    welcomeToVotly: 'Welcome to Votly',
    twoQuestions: 'Two quick questions and you\'ll have a workflow ready to launch.',
    whatsYourRole: 'What\'s your role?',
    whatFeedbackMatters: 'What feedback matters most right now?',
    stepOf: (current: number, total: number) => `${current} of ${total}`,
    next: 'Next',
    back: 'Back',
    getStarted: 'Launch my workspace',
    launching: 'Launching…',
    v1Note: 'Votly V1 is optimized for developers.',
    // Roles
    developer: 'Developer',
    brokerage: 'Brokerage',
    propertyManager: 'Property Manager',
    assetManager: 'Asset Manager',
    other: 'Other',
    // Priorities
    handoverExperience: 'Handover experience',
    snaggingResolution: 'Snagging resolution',
    both: 'Both',
    handoverDesc: 'Capture buyer sentiment at the moment of key handover.',
    snaggingDesc: 'Track snag reporting and resolution satisfaction.',
    bothDesc: 'Full-cycle feedback from handover through snagging.',
    // Navigation
    overview: 'Overview',
    programs: 'Programs',
    workflows: 'Workflows',
    stakeholders: 'Stakeholders',
    communities: 'Communities',
    kiosk: 'Kiosk',
    settings: 'Settings',
    signOut: 'Sign out',
    // Dashboard
    dashboard: 'Dashboard',
    goodMorning: 'Good morning',
    goodAfternoon: 'Good afternoon',
    goodEvening: 'Good evening',
    responseRate: 'Response Rate',
    npsScore: 'NPS Score',
    openSnags: 'Open Snags',
    snagResolutionRate: 'Snag Resolution Rate',
    activePrograms: 'Active Programs',
    avgHandoverScore: 'Avg Handover Score',
    weeklyResponseTrend: 'Weekly Response Trend',
    recentPrograms: 'Recent Programs',
    recentActivity: 'Recent Activity',
    vsLastWeek: 'vs last week',
    buyers: 'buyers',
    viewAll: 'View all',
    createProgram: 'Create Program',
    // Programs
    allPrograms: 'All Programs',
    programName: 'Program name',
    project: 'Project',
    workflow: 'Workflow',
    sent: 'Sent',
    responded: 'Responded',
    nps: 'NPS',
    status: 'Status',
    createdDate: 'Created',
    active: 'Active',
    draft: 'Draft',
    paused: 'Paused',
    closed: 'Closed',
    archived: 'Archived',
    allStatuses: 'All statuses',
    searchPrograms: 'Search programs…',
    newProgram: 'New Program',
    // Workflows
    allWorkflows: 'All Workflows',
    handover: 'Handover',
    snagging: 'Snagging',
    questionCount: 'questions',
    languages: 'Languages',
    lastUsed: 'Last used',
    newWorkflow: 'New Workflow',
    previewWorkflow: 'Preview',
    duplicateWorkflow: 'Duplicate',
    archiveWorkflow: 'Archive',
    noWorkflowsFound: 'No workflows found',
    noWorkflowsDesc: 'Create your first workflow to start collecting buyer feedback.',
    // Settings
    workspace: 'Workspace',
    team: 'Team',
    notifications: 'Notifications',
    integrations: 'Integrations',
    workspaceName: 'Workspace name',
    industry: 'Industry',
    defaultLanguage: 'Default language',
    saveChanges: 'Save changes',
    saving: 'Saving…',
    inviteMember: 'Invite member',
    memberEmail: 'Member email',
    role: 'Role',
    owner: 'Owner',
    admin: 'Admin',
    member: 'Member',
    emailNotifications: 'Email notifications',
    smsNotifications: 'SMS notifications',
    whatsappNotifications: 'WhatsApp notifications',
    notifyOnResponse: 'Notify on new response',
    notifyOnSnagUpdate: 'Notify on snag update',
    notifyWeeklyDigest: 'Weekly digest',
    resendIntegration: 'Resend (Email)',
    twilioIntegration: 'Twilio (SMS)',
    whatsappIntegration: 'WhatsApp Business',
    connected: 'Connected',
    notConnected: 'Not connected',
    connect: 'Connect',
    disconnect: 'Disconnect',
    changesSaved: 'Changes saved',
    inviteSent: 'Invitation sent',
  },
  ar: {
    // Auth
    signIn: 'تسجيل الدخول',
    signUp: 'إنشاء حساب',
    email: 'البريد الإلكتروني',
    password: 'كلمة المرور',
    confirmPassword: 'تأكيد كلمة المرور',
    fullName: 'الاسم الكامل',
    continueWithGoogle: 'المتابعة بحساب Google',
    alreadyHaveAccount: 'لديك حساب بالفعل؟',
    dontHaveAccount: 'ليس لديك حساب؟',
    forgotPassword: 'نسيت كلمة المرور؟',
    orContinueWith: 'أو المتابعة بـ',
    rememberMe: 'تذكّرني',
    signingIn: 'جارٍ تسجيل الدخول…',
    creatingAccount: 'جارٍ إنشاء الحساب…',
    invalidCredentials: 'بيانات غير صحيحة — استخدم الحسابات التجريبية أدناه',
    emailRequired: 'البريد الإلكتروني مطلوب',
    emailInvalid: 'أدخل بريدًا إلكترونيًا صحيحًا',
    passwordRequired: 'كلمة المرور مطلوبة',
    passwordMin: 'يجب أن تكون كلمة المرور 8 أحرف على الأقل',
    nameRequired: 'الاسم الكامل مطلوب',
    passwordMismatch: 'كلمتا المرور غير متطابقتين',
    demoCredentials: 'بيانات تجريبية',
    useDemoAccount: 'استخدام الحساب التجريبي',
    // Onboarding
    welcomeToVotly: 'مرحبًا بك في Votly',
    twoQuestions: 'سؤالان سريعان وسيكون لديك سير عمل جاهز للإطلاق.',
    whatsYourRole: 'ما هو دورك؟',
    whatFeedbackMatters: 'ما هي الملاحظات الأكثر أهمية الآن؟',
    stepOf: (current: number, total: number) => `${current} من ${total}`,
    next: 'التالي',
    back: 'رجوع',
    getStarted: 'إطلاق مساحة العمل',
    launching: 'جارٍ الإطلاق…',
    v1Note: 'Votly V1 محسّن للمطوّرين.',
    // Roles
    developer: 'المطوّر',
    brokerage: 'وسيط',
    propertyManager: 'مدير عقارات',
    assetManager: 'مدير أصول',
    other: 'أخرى',
    // Priorities
    handoverExperience: 'تجربة التسليم',
    snaggingResolution: 'حل العيوب',
    both: 'كلاهما',
    handoverDesc: 'التقاط مشاعر المشتري في لحظة التسليم الرئيسية.',
    snaggingDesc: 'تتبّع الإبلاغ عن العيوب ورضا حلّها.',
    bothDesc: 'ملاحظات دورة كاملة من التسليم إلى العيوب.',
    // Navigation
    overview: 'نظرة عامة',
    programs: 'البرامج',
    workflows: 'سير العمل',
    stakeholders: 'أصحاب المصلحة',
    communities: 'المجتمعات',
    kiosk: 'الكشك',
    settings: 'الإعدادات',
    signOut: 'تسجيل الخروج',
    // Dashboard
    dashboard: 'لوحة التحكم',
    goodMorning: 'صباح الخير',
    goodAfternoon: 'مساء النور',
    goodEvening: 'مساء الخير',
    responseRate: 'معدل الاستجابة',
    npsScore: 'نقاط NPS',
    openSnags: 'العيوب المفتوحة',
    snagResolutionRate: 'معدل حل العيوب',
    activePrograms: 'البرامج النشطة',
    avgHandoverScore: 'متوسط نقاط التسليم',
    weeklyResponseTrend: 'اتجاه الاستجابة الأسبوعي',
    recentPrograms: 'البرامج الأخيرة',
    recentActivity: 'النشاط الأخير',
    vsLastWeek: 'مقارنةً بالأسبوع الماضي',
    buyers: 'مشترٍ',
    viewAll: 'عرض الكل',
    createProgram: 'إنشاء برنامج',
    // Programs
    allPrograms: 'جميع البرامج',
    programName: 'اسم البرنامج',
    project: 'المشروع',
    workflow: 'سير العمل',
    sent: 'المُرسَل',
    responded: 'استجاب',
    nps: 'NPS',
    status: 'الحالة',
    createdDate: 'تاريخ الإنشاء',
    active: 'نشط',
    draft: 'مسودة',
    paused: 'موقوف',
    closed: 'مغلق',
    archived: 'مؤرشف',
    allStatuses: 'جميع الحالات',
    searchPrograms: 'بحث في البرامج…',
    newProgram: 'برنامج جديد',
    // Workflows
    allWorkflows: 'جميع سير العمل',
    handover: 'التسليم',
    snagging: 'العيوب',
    questionCount: 'أسئلة',
    languages: 'اللغات',
    lastUsed: 'آخر استخدام',
    newWorkflow: 'سير عمل جديد',
    previewWorkflow: 'معاينة',
    duplicateWorkflow: 'تكرار',
    archiveWorkflow: 'أرشفة',
    noWorkflowsFound: 'لا توجد سير عمل',
    noWorkflowsDesc: 'أنشئ سير عمل أول لبدء جمع ملاحظات المشترين.',
    // Settings
    workspace: 'مساحة العمل',
    team: 'الفريق',
    notifications: 'الإشعارات',
    integrations: 'التكاملات',
    workspaceName: 'اسم مساحة العمل',
    industry: 'القطاع',
    defaultLanguage: 'اللغة الافتراضية',
    saveChanges: 'حفظ التغييرات',
    saving: 'جارٍ الحفظ…',
    inviteMember: 'دعوة عضو',
    memberEmail: 'بريد العضو الإلكتروني',
    role: 'الدور',
    owner: 'مالك',
    admin: 'مشرف',
    member: 'عضو',
    emailNotifications: 'إشعارات البريد الإلكتروني',
    smsNotifications: 'إشعارات الرسائل القصيرة',
    whatsappNotifications: 'إشعارات واتساب',
    notifyOnResponse: 'إشعار عند الاستجابة الجديدة',
    notifyOnSnagUpdate: 'إشعار عند تحديث العيب',
    notifyWeeklyDigest: 'ملخص أسبوعي',
    resendIntegration: 'Resend (بريد إلكتروني)',
    twilioIntegration: 'Twilio (رسائل قصيرة)',
    whatsappIntegration: 'واتساب للأعمال',
    connected: 'متصل',
    notConnected: 'غير متصل',
    connect: 'اتصال',
    disconnect: 'قطع الاتصال',
    changesSaved: 'تم حفظ التغييرات',
    inviteSent: 'تم إرسال الدعوة',
  },
} as const;

export type TranslationKey = keyof typeof translations.en;

export function t(lang: Lang, key: TranslationKey, ...args: number[]): string {
  const val = translations[lang][key];
  if (typeof val === 'function') {
    return (val as (...a: number[]) => string)(...args);
  }
  return val as string;
}